import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { camelizeKeys, decamelizeKeys } from 'humps'
import { stringifyQuery } from 'utils/strings'
import i18next from 'i18next'

const KEEP_UNUSED_DATA_FOR_SECONDS = 0
const CSRF_TOKEN = 'x-csrf-token'

class ServerError extends Error {
  constructor (response, errorMessage) {
    super(errorMessage)
    this.name = this.constructor.name
    this.status = response.status
  }
}

const prepareHeaders = (headers) => {
  headers.append('Content-Type', 'application/json')
  headers.append('Accept', 'application/json')

  return addCsrfTokenToHeaders(headers)
}

const addCsrfTokenToHeaders = (headers) => {
  headers.append(CSRF_TOKEN, sessionStorage.getItem(CSRF_TOKEN))

  return headers
}

const prepareUrl = (path, query) => {
  const concatenator = (path.indexOf('?') === -1) ? '?' : '&'
  const pathWithQuery = query ? `${path}${concatenator}` + stringifyQuery(decamelizeKeys(query)) : path
  const url = `${process.env.API_ENDPOINT_UI}${pathWithQuery}`
  return url
}

const getDecamelizedArgs = (args) => {
  let newArgs = args
  if (args?.body) {
    newArgs = {
      ...args,
      body: decamelizeKeys(args.body),
    }
  }
  if (args?.params) {
    newArgs = {
      ...args,
      params: stringifyQuery(decamelizeKeys(args.params)),
    }
  }
  return newArgs
}

// output format:
//   data: {
//      someData: ...,   // optional for errors
//      message: {       // for errors/warnings from server or errors from fetchBaseQuery()
//         type: 'error',
//         text: '...',
//         status: 401,
//      },
//      error: true/false
//   }
const normalizeErrorMessage = (baseResult) => {
  const data = baseResult.data || {}

  // e.g. 404 or 401 error
  if (baseResult.error) {
    const errData = baseResult.error.data
    data.message = {
      type: errData?.message?.type || 'error',
      text: errData?.message?.text || JSON.stringify(errData),
      status: baseResult.error.status,
    }
    delete data.error
  }
  data.error = data.message?.type === 'error'

  return ({ data: data })
}

const storeCSRFToken = (token) => {
  if (token) {
    sessionStorage.setItem(CSRF_TOKEN, token)
  }
}

const baseQuery = async (args, api, extraOptions) => {
  let newArgs = args
  if (!extraOptions?.isFileUpload) {
    newArgs = getDecamelizedArgs(args)
  }

  const customFetchBaseQuery = fetchBaseQuery({
    baseUrl: process.env.API_ENDPOINT_UI,
    prepareHeaders: extraOptions?.isFileUpload ? addCsrfTokenToHeaders : prepareHeaders,
    fetchFn: (input) => {
      return fetch(input, { credentials: 'include' })
    },
  })
  const baseResult = await customFetchBaseQuery(newArgs, api, extraOptions)

  storeCSRFToken(baseResult.meta.response.headers.get(CSRF_TOKEN))

  let result = baseResult

  if (!extraOptions?.noCamelize) {
    result = camelizeKeys(baseResult)
  }

  result = normalizeErrorMessage(result)
  return result
}

const normalizeUploadErrorMessage = (event) => {
  const responseText = event.target.responseText
  let data = {}
  let errorText
  try {
    data = JSON.parse(responseText)
  } catch (err) {
    errorText = i18next.t('common.uploadError')
    throw new ServerError(event.target, errorText)
  }
  data.error = data.message?.type === 'error'
  return ({ data: data })
}

export const uploadWithProgressQuery = async ({
  url, body, bodyParamName, query,
  onUploadProgress, uploadAbortCallback,
}) => {
  const fullUrl = prepareUrl(url, query)

  let result
  try {
    result = await fileUploadRequest({ url: fullUrl, body, bodyParamName, onUploadProgress, uploadAbortCallback })
    storeCSRFToken(result.target.getResponseHeader(CSRF_TOKEN))

    result = normalizeUploadErrorMessage(result)
    result = camelizeKeys(result)
  } catch (error) {
    return ({
      data: {
        error: true,
        message: {
          type: 'error',
          text: error.message,
          status: error.status,
        },
      },
    })
  }

  return result
}

const fileUploadRequest = ({ url, body, bodyParamName, onUploadProgress, uploadAbortCallback }) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    xhr.setRequestHeader(CSRF_TOKEN, sessionStorage.getItem(CSRF_TOKEN))
    xhr.onload = (event) => resolve(event)
    xhr.onerror = reject
    if (xhr.upload && onUploadProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onUploadProgress(e, body.get(bodyParamName))
        }
      }
    }
    xhr.withCredentials = true
    xhr.send(body)

    if (uploadAbortCallback) {
      const abortFunction = () => {
        xhr.abort()
      }
      uploadAbortCallback(abortFunction)
    }
  })
}

export const api = createApi({
  baseQuery: baseQuery,
  keepUnusedDataFor: KEEP_UNUSED_DATA_FOR_SECONDS,
  tagTypes: [],
  endpoints: () => ({}),
})
